import { Modal, Form, Button } from "react-bootstrap";
import "../../assets/scss/new-entity-modal.scss";
import { useQuery } from "react-query";
import { useState, useEffect } from "react";
import { format } from "date-fns";
import ReactPaginate from "react-paginate";
import { paginationOptions } from "../../utils/helpers";
import queryString from "query-string";

import { useAuth } from "../../hooks/useAuth";
import { fetchActionsUtil } from "../../utils/helpers";
import NoTableItem from "../utils/NoTableItem";
import PreviewFormOnlyComponent from "../Requisition/PreviewFormOnlyComponent";
import { ApproveCheckIcon } from "../Icons";
import useDebounce from "../../utils/hooks";
import CalendarIcon from "mdi-react/CalendarIcon";

export default function FormSelectorModal({
  showchildForm,
  setShowChildForm,
  handleSelectedForms, // renamed: now receives array of selected forms
}) {
  const { backendUrl } = useAuth();

  const [forms, setForms] = useState([]);
  const [selectedForms, setSelectedForms] = useState([]); // array for multi-select
  const [previewForm, setPreviewForm] = useState(null); // form being previewed
  const [iframeReady, setIframeReady] = useState(false);

  const [filterParams, setFilterParams] = useState({
    limit: 12,
    page: 1,
    count: 12,
    q: "",
  });

  const [queryParams, setQueryParams] = useState({ ...filterParams });

  const { data, isSuccess, isLoading, isFetched } = useQuery(
    ["GET-FORMS", queryParams],
    async () =>
      await fetchActionsUtil(
        `${backendUrl}/api/forms?${queryString.stringify(queryParams)}`,
        "GET"
      ),
    {
      onSuccess: data => {
        const fetchedForms = data?.data?.forms ?? [];
        setForms(fetchedForms);

        // Auto-preview first form if available
        if (fetchedForms.length > 0 && !previewForm) {
          setPreviewForm(fetchedForms[0]);
        }
      },
    }
  );

  const debouncedFilterParams = useDebounce(filterParams, 500);

  useEffect(() => {
    setQueryParams(prev => ({ ...prev, ...debouncedFilterParams }));
  }, [debouncedFilterParams]);

  const handleSearchQueryChange = e => {
    const { name, value } = e.target;
    setFilterParams(prev => ({ ...prev, [name]: value }));
  };

  const isSelected = id => selectedForms.some(f => f.id === id);

  const toggleFormSelection = form => {
    setPreviewForm(form);
    setSelectedForms(prev =>
      isSelected(form.id) ? prev.filter(f => f.id !== form.id) : [...prev, form]
    );
  };

  const handleConfirm = () => {
    handleSelectedForms(selectedForms);
    setShowChildForm(false);
  };

  const handleCancel = () => {
    handleSelectedForms([]);
    setShowChildForm(false);
  };

  return (
    <Modal
      show={showchildForm}
      onHide={() => setShowChildForm(false)}
      centered
      animation={false}
      enforceFocus={false}
      fullscreen={true}
    >
      <Modal.Header
        className="d-flex justify-content-between gap-3"
        closeButton
      >
        <Modal.Title className="mx-0 text-nowrap">
          <h5>Select Forms</h5>
        </Modal.Title>
        <Form.Control
          name="q"
          className="w-25"
          value={filterParams.q}
          onChange={handleSearchQueryChange}
          placeholder="Search by title"
        />
      </Modal.Header>

      <Modal.Body>
        <div className="filter-body row">
          {/* Form List */}
          <div className="col-6">
            {isLoading && <p className="text-center">Please wait...</p>}
            <div className="row g-3">
              {!isLoading && forms.length === 0 ? (
                <NoTableItem />
              ) : (
                forms.map(form => (
                  <div className="col-md-4" key={form.id}>
                    <div
                      className={`card p-cursor h-100 w-100 position-relative ${
                        isSelected(form.id)
                          ? "border border-primary shadow-sm"
                          : ""
                      } ${
                        previewForm?.id === form.id && !isSelected(form.id)
                          ? "border border-primary-subtle"
                          : ""
                      }`}
                      onClick={() => toggleFormSelection(form)}
                    >
                      <div className="bg-white border-0 p-2 pb-0 d-flex justify-content-between align-items-center card-header">
                        <small className="text-muted">
                          <CalendarIcon size={13} />{" "}
                          {form.Date_Log
                            ? format(new Date(form.Date_Log), "dd MMM, yyyy")
                            : "..."}
                        </small>
                        {isSelected(form.id) && (
                          <div className="position-absolute end-0 m-1">
                            <ApproveCheckIcon />
                          </div>
                        )}
                      </div>
                      <div className="card-body p-2">
                        <div className="fw-bolder card-title h5">
                          {form.Title || "..."}
                        </div>
                        <div className="card-subtitle mb-2 text-muted fw-normal">
                          Revision: {form.revision || "..."}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            <div className="d-flex justify-content-between px-0 align-items-center pagination p-0 mt-3">
              <div className="pagination_left d-flex gap-3 align-items-center">
                <select
                  value={filterParams.limit}
                  name="limit"
                  className="form-select"
                  onChange={handleSearchQueryChange}
                >
                  <option value="10">10 rows</option>
                  <option value="20">20 rows</option>
                  <option value="30">30 rows</option>
                  <option value="40">40 rows</option>
                </select>
              </div>
              <ReactPaginate
                {...paginationOptions}
                pageCount={Math.ceil(queryParams.count / queryParams.limit)}
                marginPagesDisplayed={2}
                pageRangeDisplayed={0}
                onPageChange={({ selected }) => {
                  setQueryParams(prev => ({ ...prev, page: selected + 1 }));
                }}
                forcePage={queryParams.page - 1}
              />
            </div>
          </div>

          {/* Preview Panel */}
          <div className="col-6">
            {isFetched && previewForm && (
              <PreviewFormOnlyComponent
                selectedFilledForm={previewForm}
                template={previewForm?.Template}
                setFetchingIframe={setIframeReady}
                size="30rem"
                canFill={false}
                showTableCheckbox={false}
              />
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex gap-3 align-items-center">
          {selectedForms.length > 0 && (
            <small className="text-muted">
              {selectedForms.length} form{selectedForms.length > 1 ? "s" : ""}{" "}
              selected
            </small>
          )}
          <Button variant="outline-primary" size="lg" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleConfirm}
            disabled={selectedForms.length === 0}
          >
            Continue
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
